import { api } from "@lib/orpc-client";
import { getLegacyErrorBody, getORPCErrorCode } from "@lib/orpc-error";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { TonWalletUtils } from "@lib/secrets";
import * as Sentry from "@sentry/react";
import { CaptchaRequiredResponse } from "../types/captcha";

export type Destination = "bank" | "e-wallet" | "pay-bank";

type InitiateRedemptionRequestParams = {
  ui_amount: number;
  destination_uuid: string;
  destination: Destination;
  memo?: string;
  captcha_key?: string;
  captcha_value?: { slide_x: number; slide_y: number };
};

type InitiateP2PTransferParams = {
  ui_amount: number;
  destination_address: string;
  address_name?: string;
  save_destination?: boolean;
};

type InitiatedRedemptionRequestResponse = Awaited<
  ReturnType<typeof api.walletV2.redeem.initiate>
>;
type SentRedemptionRequestResponse = Awaited<
  ReturnType<typeof api.walletV2.redeem.toBank>
>;
type InitiatedP2PTransferResponse = Awaited<
  ReturnType<typeof api.walletV2.transfer.initiate>
>;
type SentP2PTransferResponse = Awaited<
  ReturnType<typeof api.walletV2.transfer.send>
>;
type PpobOrderResponse = Awaited<
  ReturnType<typeof api.wallet.ppob.prepaidOrders>
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isCaptchaData(
  value: unknown
): value is CaptchaRequiredResponse["data"] {
  return (
    isRecord(value) &&
    typeof value.captcha_key === "string" &&
    typeof value.master_image_base64 === "string" &&
    typeof value.thumb_image_base64 === "string" &&
    typeof value.master_width === "number" &&
    typeof value.master_height === "number" &&
    typeof value.thumb_width === "number" &&
    typeof value.thumb_height === "number" &&
    typeof value.display_x === "number" &&
    typeof value.display_y === "number"
  );
}

function toCaptchaResponse(
  value: unknown
): CaptchaRequiredResponse | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  if (value.captcha_required === true && isCaptchaData(value.data)) {
    return {
      captcha_required: true,
      data: value.data,
    };
  }

  if (isCaptchaData(value)) {
    return {
      captcha_required: true,
      data: value,
    };
  }

  return undefined;
}

function isSignedRedeemRequestResponse(
  value: InitiatedRedemptionRequestResponse | CaptchaRequiredResponse
): value is Exclude<
  InitiatedRedemptionRequestResponse,
  CaptchaRequiredResponse
> {
  return (
    isRecord(value) &&
    typeof value.redeemRequestUuid === "string" &&
    typeof value.seqNo === "number" &&
    typeof value.serializedTransaction === "string"
  );
}

function isPpobOrderResponse(value: unknown): value is PpobOrderResponse {
  return (
    isRecord(value) &&
    isRecord(value.data) &&
    isRecord(value.data.escrow) &&
    typeof value.data.escrow.escrowUuid === "string" &&
    typeof value.data.escrow.unsignedTx === "string" &&
    isRecord(value.data.escrow.additionalData) &&
    typeof value.data.escrow.additionalData.seqNo === "number"
  );
}

const useManageTransactionRequest = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<unknown>(null);

  const translateServerError = (caughtError: unknown) => {
    const errorCode = getORPCErrorCode(caughtError);

    if (errorCode) {
      const translationKey = `errors.${errorCode}.description`;
      const translatedMessage = t(translationKey);
      return translatedMessage !== translationKey
        ? translatedMessage
        : t("errors.genericServerDescription");
    }

    return t("errors.genericClientDescription");
  };

  const initiateRedemptionRequest = async ({
    ui_amount,
    destination,
    destination_uuid,
    memo,
    captcha_key,
    captcha_value,
  }: InitiateRedemptionRequestParams): Promise<
    InitiatedRedemptionRequestResponse | CaptchaRequiredResponse
  > => {
    try {
      const response = await api.walletV2.redeem.initiate({
        ui_amount,
        destination,
        destination_uuid,
        memo,
        ...(captcha_key && captcha_value
          ? {
              captcha_key,
              captcha_value,
            }
          : {}),
      });

      return toCaptchaResponse(response) ?? response;
    } catch (caughtError) {
      const captchaResponse = toCaptchaResponse(
        getLegacyErrorBody(caughtError)
      );
      if (captchaResponse) {
        return captchaResponse;
      }

      throw caughtError;
    }
  };

  const sendBankSerializedTransaction = async (
    signedTransaction: string,
    uuid: string
  ): Promise<SentRedemptionRequestResponse> => {
    return await api.walletV2.redeem.toBank({ uuid, data: signedTransaction });
  };

  const sendEWalletSerializedTransaction = async (
    signedTransaction: string,
    uuid: string
  ): Promise<SentRedemptionRequestResponse> => {
    return await api.walletV2.redeem.toEwallet({
      uuid,
      data: signedTransaction,
    });
  };

  const sendPaymentBankSerializedTransaction = async (
    signedTransaction: string,
    uuid: string
  ) => {
    return await api.walletV2.pay.bankTransferSend({
      uuid,
      data: signedTransaction,
    });
  };

  const submitPpobEscrowDeposit = async (
    signedTx: string,
    escrowUuid: string
  ) => {
    return await api.wallet.escrow.submitDeposit({ escrowUuid, signedTx });
  };

  const requestBankRedemption = async (
    params: InitiateRedemptionRequestParams
  ) => {
    try {
      setIsLoading(true);
      const initiateResponse = await initiateRedemptionRequest(params);

      if (!isSignedRedeemRequestResponse(initiateResponse)) {
        return initiateResponse;
      }

      const signedTransaction = await TonWalletUtils.signMessage(
        initiateResponse.serializedTransaction,
        initiateResponse.seqNo
      );

      const response = await sendBankSerializedTransaction(
        signedTransaction,
        initiateResponse.redeemRequestUuid
      );
      setData(response);
      return response;
    } catch (caughtError) {
      Sentry.captureException(caughtError);

      if (getORPCErrorCode(caughtError) === 2015) {
        throw caughtError;
      }

      const errorMessage = translateServerError(caughtError);
      setError(errorMessage);
      throw errorMessage;
    } finally {
      setIsLoading(false);
    }
  };

  const requestEWalletRedemption = async (
    params: InitiateRedemptionRequestParams
  ) => {
    try {
      setIsLoading(true);
      const initiateResponse = await initiateRedemptionRequest(params);

      if (!isSignedRedeemRequestResponse(initiateResponse)) {
        return initiateResponse;
      }

      const signedTransaction = await TonWalletUtils.signMessage(
        initiateResponse.serializedTransaction,
        initiateResponse.seqNo
      );

      const response = await sendEWalletSerializedTransaction(
        signedTransaction,
        initiateResponse.redeemRequestUuid
      );
      setData(response);
      return response;
    } catch (caughtError) {
      Sentry.captureException(caughtError);

      if (getORPCErrorCode(caughtError) === 2015) {
        throw caughtError;
      }

      const errorMessage = translateServerError(caughtError);
      setError(errorMessage);
      throw errorMessage;
    } finally {
      setIsLoading(false);
    }
  };

  const requestPaymentBankRedemption = async (
    params: InitiateRedemptionRequestParams
  ) => {
    try {
      setIsLoading(true);
      const initiateResponse = await initiateRedemptionRequest(params);

      if (!isSignedRedeemRequestResponse(initiateResponse)) {
        return initiateResponse;
      }

      const signedTransaction = await TonWalletUtils.signMessage(
        initiateResponse.serializedTransaction,
        initiateResponse.seqNo
      );
      const response = await sendPaymentBankSerializedTransaction(
        signedTransaction,
        initiateResponse.redeemRequestUuid
      );
      setData(response);
      return response;
    } catch (caughtError) {
      Sentry.captureException(caughtError);
      const errorMessage = translateServerError(caughtError);
      setError(errorMessage);
      throw errorMessage;
    } finally {
      setIsLoading(false);
    }
  };

  const initiateP2PTransfer = async (
    params: InitiateP2PTransferParams
  ): Promise<InitiatedP2PTransferResponse> => {
    return await api.walletV2.transfer.initiate(params);
  };

  const sendP2PTransferSerializedTransaction = async (
    signedTransaction: string,
    uuid: string,
    save_destination?: boolean,
    address_name?: string
  ): Promise<SentP2PTransferResponse> => {
    return await api.walletV2.transfer.send({
      data: signedTransaction,
      uuid,
      save_destination,
      address_name,
    });
  };

  const requestP2PTransfer = async (params: InitiateP2PTransferParams) => {
    try {
      setIsLoading(true);
      const { uuid, seqNo, serializedTransaction } = await initiateP2PTransfer(
        params
      );

      const signedTransaction = await TonWalletUtils.signMessage(
        serializedTransaction,
        seqNo
      );

      const response = await sendP2PTransferSerializedTransaction(
        signedTransaction,
        uuid,
        params.save_destination,
        params.address_name
      );
      setData(response);
      return response;
    } catch (caughtError) {
      Sentry.captureException(caughtError);
      const errorMessage = translateServerError(caughtError);
      setError(errorMessage);
      throw errorMessage;
    } finally {
      setIsLoading(false);
    }
  };

  const requestPrepaidPayment = async (
    buyerSkuCode: string,
    customerNo: string,
    captchaData?: {
      captcha_key: string;
      captcha_value: { slide_x: number; slide_y: number };
    }
  ) => {
    try {
      setIsLoading(true);

      const initiateResponse = await api.wallet.ppob.prepaidOrders({
        buyer_sku_code: buyerSkuCode,
        customer_no: customerNo,
        ...(captchaData
          ? {
              captcha_key: captchaData.captcha_key,
              captcha_value: captchaData.captcha_value,
            }
          : {}),
      });

      const captchaResponse = toCaptchaResponse(initiateResponse);
      if (captchaResponse) {
        return captchaResponse;
      }

      if (!isPpobOrderResponse(initiateResponse)) {
        throw new Error("Unexpected prepaid order response");
      }

      const escrow = initiateResponse.data.escrow;
      const signedTransaction = await TonWalletUtils.signMessage(
        escrow.unsignedTx,
        escrow.additionalData.seqNo
      );

      const response = await submitPpobEscrowDeposit(
        signedTransaction,
        escrow.escrowUuid
      );
      setData(response);
      return response;
    } catch (caughtError) {
      const captchaResponse = toCaptchaResponse(
        getLegacyErrorBody(caughtError)
      );
      if (captchaResponse) {
        return captchaResponse;
      }

      Sentry.captureException(caughtError);
      const errorMessage = translateServerError(caughtError);
      setError(errorMessage);
      throw errorMessage;
    } finally {
      setIsLoading(false);
    }
  };

  const requestPostpaidPayment = async (
    buyerSkuCode: string,
    customerNo: string,
    inquiryQuote: string,
    captchaData?: {
      captcha_key: string;
      captcha_value: { slide_x: number; slide_y: number };
    }
  ) => {
    try {
      setIsLoading(true);

      const initiateResponse = await api.wallet.ppob.postpaidOrders({
        buyer_sku_code: buyerSkuCode,
        customer_no: customerNo,
        ppob_inquiry_quote_uuid: inquiryQuote,
        ...(captchaData
          ? {
              captcha_key: captchaData.captcha_key,
              captcha_value: captchaData.captcha_value,
            }
          : {}),
      });

      const captchaResponse = toCaptchaResponse(initiateResponse);
      if (captchaResponse) {
        return captchaResponse;
      }

      if (!isPpobOrderResponse(initiateResponse)) {
        throw new Error("Unexpected postpaid order response");
      }

      const escrow = initiateResponse.data.escrow;
      const signedTransaction = await TonWalletUtils.signMessage(
        escrow.unsignedTx,
        escrow.additionalData.seqNo
      );

      const response = await submitPpobEscrowDeposit(
        signedTransaction,
        escrow.escrowUuid
      );
      setData(response);
      return response;
    } catch (caughtError) {
      const captchaResponse = toCaptchaResponse(
        getLegacyErrorBody(caughtError)
      );
      if (captchaResponse) {
        return captchaResponse;
      }

      Sentry.captureException(caughtError);
      const errorMessage = translateServerError(caughtError);
      setError(errorMessage);
      throw errorMessage;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    data,
    isLoading,
    error,
    requestBankRedemption,
    requestEWalletRedemption,
    requestPaymentBankRedemption,
    requestP2PTransfer,
    requestPrepaidPayment,
    requestPostpaidPayment,
  };
};

export default useManageTransactionRequest;
